// ===================== Platform abstraction (web ⇄ native) =====================
// One seam between the game and anything platform-specific: haptics, rewarded
// ads, in-app purchases, and ad consent. The web build ships no native code —
// everything here degrades to a safe no-op. Inside a Capacitor shell the same
// bundle detects the installed plugins at runtime and lights the features up.
//
// Native plugin expectations (see docs/PUBLISHING.md):
//   Haptics   — @capacitor/haptics
//   Ads       — @capacitor-community/admob (includes UMP consent flow)
//   Purchases — @revenuecat/purchases-capacitor

const cap = () => window.Capacitor;

// The game bundle deliberately ships no Capacitor JS (zero runtime deps), so
// `Capacitor.Plugins` — which only @capacitor/core's registerPlugin() fills —
// is EMPTY inside the native shell, and `isPluginAvailable()` reports every
// plugin as missing. The bridge does inject everything needed to reach native
// directly: `PluginHeaders` (installed plugins + method signatures) and the
// `nativePromise`/`nativeCallback` transport. Build the method proxies from
// those, the same way registerPlugin() would have.
const _proxies = {};
const plugin = (name) => {
  try {
    const c = cap();
    if (!c) return null;
    if (c.Plugins && c.Plugins[name]) return c.Plugins[name];  // a bundled runtime registered it
    if (_proxies[name]) return _proxies[name];
    const header = (c.PluginHeaders || []).find((h) => h.name === name);
    if (!header || !c.nativePromise || !c.nativeCallback) return null;
    const p = {};
    for (const m of header.methods) {
      if (m.name === 'addListener' || m.name === 'removeListener') continue;
      p[m.name] = m.rtype === 'promise'
        ? (options) => c.nativePromise(name, m.name, options)
        : (options, callback) => c.nativeCallback(name, m.name, options, callback);
    }
    p.addListener = (eventName, callback) => {
      const callbackId = c.nativeCallback(name, 'addListener', { eventName }, callback);
      return {
        remove: () => {
          try { c.nativeCallback(name, 'removeListener', { eventName, callbackId }, () => {}); }
          catch { /* bridge already gone */ }
        },
      };
    };
    _proxies[name] = p;
    return p;
  } catch { return null; }
};

export const Platform = {
  get isNative() {
    try { return !!(cap() && cap().isNativePlatform && cap().isNativePlatform()); }
    catch { return false; }
  },

  // Called once at boot. Never throws; failures just leave features off.
  async init() {
    if (!this.isNative) return;
    try { await this.ads._init(); } catch { /* ads stay off */ }
    try { await this.iap._init(); } catch { /* iap stays off */ }
  },

  // ------------------------------------------------------------------ haptics
  // kinds: 'light' (hits/UI), 'medium' (level-up, chest), 'heavy' (boss, death)
  haptic(kind = 'light') {
    const h = plugin('Haptics');
    if (h) {
      const style = kind === 'heavy' ? 'HEAVY' : kind === 'medium' ? 'MEDIUM' : 'LIGHT';
      h.impact({ style }).catch(() => {});
      return;
    }
    // Web fallback (Android browsers; iOS Safari ignores it).
    if (navigator.vibrate) {
      navigator.vibrate(kind === 'heavy' ? 60 : kind === 'medium' ? 30 : 12);
    }
  },

  // --------------------------------------------------------------------- ads
  // Rewarded-only by design: revives and coin doublers, always opt-in.
  ads: {
    available: false,
    _admob: null,
    async _init() {
      this._admob = plugin('AdMob');
      if (!this._admob) return;
      // SDK first, then the UMP consent flow (the plugin's documented order).
      await this._admob.initialize({});
      try {
        const consent = await this._admob.requestConsentInfo();
        if (consent && consent.isConsentFormAvailable && consent.status === 'REQUIRED') {
          await this._admob.showConsentForm();
        }
      } catch { /* consent errors shouldn't kill ads outright */ }
      this.available = true;
    },
    // Resolves { rewarded: boolean }. `placement` tags analytics/mediation.
    async showRewarded(placement) {
      if (!this.available || !this._admob) return { rewarded: false };
      const subs = [];
      const on = (ev, fn) => { try { subs.push(this._admob.addListener(ev, fn)); } catch { /* events unavailable */ } };
      try {
        // Ad units are PER-PLATFORM in AdMob — an Android unit never serves
        // on iOS — so the id is picked by the running platform.
        const platform = cap().getPlatform ? cap().getPlatform() : 'android';
        const adId = AD_UNITS.rewarded[platform] || AD_UNITS.rewarded.android;
        let rewarded = false;
        // showRewardVideoAd()'s promise settles ONLY when the reward is
        // earned — an ad closed early never resolves it, which would hang the
        // death/results screen. Watch the lifecycle events and settle on
        // dismissal ourselves; the reward event fires before Dismissed, so a
        // short grace lets it land first.
        const closed = new Promise((resolve) => {
          on('onRewardedVideoAdReward', () => { rewarded = true; });
          on('onRewardedVideoAdDismissed', () => setTimeout(resolve, 250));
          on('onRewardedVideoAdFailedToShow', resolve);
        });
        await this._admob.prepareRewardVideoAd({ adId });
        const shown = this._admob.showRewardVideoAd()
          .then((r) => { if (r && (r.type || r.amount)) rewarded = true; })
          .catch(() => {});
        await Promise.race([shown, closed]);
        return { rewarded, placement };
      } catch {
        return { rewarded: false };
      } finally {
        for (const s of subs) { try { s.remove(); } catch { /* already detached */ } }
      }
    },
  },

  // --------------------------------------------------------------------- IAP
  iap: {
    available: false,
    _rc: null,
    async _init() {
      this._rc = plugin('Purchases');
      if (!this._rc) return;
      const platform = cap().getPlatform ? cap().getPlatform() : 'android';
      const apiKey = platform === 'ios' ? IAP_KEYS.ios : IAP_KEYS.android;
      if (!apiKey) return;                      // keys unset → feature off
      await this._rc.configure({ apiKey });
      this.available = true;
    },
    // Live store products for our catalog. Coin packs and the supporter pack
    // are one-time products — Android's getProducts defaults to SUBSCRIPTION
    // and would return nothing without the explicit type.
    async _storeProducts() {
      const { products } = await this._rc.getProducts({
        productIdentifiers: IAP_PRODUCTS.map((p) => p.sku),
        type: 'NON_SUBSCRIPTION',
      });
      return products || [];
    },
    // Static catalog; native price strings are filled in when available.
    async products() {
      const base = IAP_PRODUCTS.map((p) => ({ ...p }));
      if (!this.available || !this._rc) return base;
      try {
        const products = await this._storeProducts();
        for (const p of base) {
          const np = products.find((x) => x.identifier === p.sku);
          if (np) p.price = np.priceString;
        }
      } catch { /* show catalog without live prices */ }
      return base;
    },
    // Resolves { ok: boolean }. Granting is the caller's job (single source of truth).
    async purchase(sku) {
      if (!this.available || !this._rc) return { ok: false };
      try {
        // purchaseStoreProduct needs the full store product — native reads
        // identifier AND productCategory; a bare {identifier} is rejected.
        const products = await this._storeProducts();
        const product = products.find((x) => x.identifier === sku);
        if (!product) return { ok: false };
        await this._rc.purchaseStoreProduct({ product });
        return { ok: true };
      } catch { return { ok: false }; }
    },
    // Apple requires a visible restore path for non-consumables.
    async restore() {
      if (!this.available || !this._rc) return { owned: [] };
      try {
        const info = await this._rc.restorePurchases();
        const ci = (info && info.customerInfo) || {};
        // Restore by product id (matches our skus); entitlement ids are a
        // fallback for stores configured to grant through entitlements.
        const known = new Set(IAP_PRODUCTS.map((p) => p.sku));
        const owned = (ci.allPurchasedProductIdentifiers || []).filter((id) => known.has(id));
        if (!owned.length && ci.entitlements && ci.entitlements.active) {
          owned.push(...Object.keys(ci.entitlements.active).filter((id) => known.has(id)));
        }
        return { owned };
      } catch { return { owned: [] }; }
    },
  },
};

// Ad UNIT ids, one per platform — both real, both the "ingame ad unlock"
// rewarded unit under publisher pub-7266582225397888 (the publisher
// app-ads.txt declares). The AdMob APP ids live in
// android/app/src/main/AndroidManifest.xml and ios/App/App/Info.plist.
export const AD_UNITS = {
  rewarded: {
    android: 'ca-app-pub-7266582225397888/5042401660',  // real — "ingame ad unlock"
    ios: 'ca-app-pub-7266582225397888/3876133968',      // real — "ingame ad unlock"
  },
};

// RevenueCat public SDK keys — set per store before release; empty = IAP off.
export const IAP_KEYS = { android: '', ios: '' };

// Product catalog. `price` is only the FALLBACK string shown before the store
// answers — the live localised price replaces it in products() — so it has to
// match the price tier actually set in Play Console / App Store Connect.
//
// The ladder is modelled against the coin economy (docs/MONETIZATION.md): a
// first-region run banks ~330–590 coins and a whole Fang Tree costs ~5,150, so
// the Pouch is a run or two, the Chest is a third of the tree, and value per
// dollar RISES with the tier (301 → 401 → 450 coins/$) instead of the supporter
// tier being strictly worse coins for the same $4.99.
export const IAP_PRODUCTS = [
  { sku: 'neonfang.coins.small', name: 'Coin Pouch', desc: '+600 coins for the meta shop', icon: '◈', coins: 600, price: '$1.99' },
  { sku: 'neonfang.coins.big', name: 'Coin Chest', desc: '+2000 coins for the meta shop', icon: '👑', coins: 2000, price: '$4.99' },
  { sku: 'neonfang.supporter', name: 'Supporter Pack', desc: 'One-time thank-you: +4500 coins now and a golden ◈ badge', icon: '💛', coins: 4500, price: '$9.99', once: true },
];
